/**
 * Trace context. A trace ties together button-click → frontend request → backend → external.
 *
 * The browser has no true async-local storage, so we model a "current root" trace that
 * is (re)started on navigation and on user interaction, and refreshed on idle. Each
 * outgoing request becomes a span under the current trace and propagates headers so the
 * backend SDK can continue the same trace.
 */
import { TRACE_HEADERS, type Cause } from '@aris/request-watcher-shared';
import { uuid } from './util.js';

interface RootTrace {
  traceId: string;
  startedAt: number;
  cause: Cause;
}

let root: RootTrace | null = null;
const ROOT_TTL_MS = 30_000; // a trace older than this is considered stale

function ensureRoot(cause: Cause = 'unknown'): RootTrace {
  const stale = !root || Date.now() - root.startedAt > ROOT_TTL_MS;
  if (stale) {
    root = { traceId: uuid(), startedAt: Date.now(), cause };
  }
  return root!;
}

/** Start a brand-new root trace (call on route change / explicit user action). */
export function startTrace(cause: Cause): string {
  root = { traceId: uuid(), startedAt: Date.now(), cause };
  return root.traceId;
}

export function currentTraceId(): string {
  return ensureRoot().traceId;
}

export interface SpanIds {
  traceId: string;
  spanId: string;
  parentSpanId: string | null;
}

export function newSpan(): SpanIds {
  const r = ensureRoot();
  return { traceId: r.traceId, spanId: uuid(), parentSpanId: null };
}

/** Header map to attach to an outgoing request for downstream correlation. */
export function traceHeaders(span: SpanIds, cause: Cause): Record<string, string> {
  return {
    [TRACE_HEADERS.traceId]: span.traceId,
    [TRACE_HEADERS.spanId]: span.spanId,
    [TRACE_HEADERS.origin]: 'frontend',
    [TRACE_HEADERS.cause]: cause,
    // W3C traceparent: version-traceid(32hex)-spanid(16hex)-flags
    traceparent: `00-${span.traceId.replace(/-/g, '')}-${span.spanId.replace(/-/g, '').slice(0, 16)}-01`,
  };
}

/** Hook DOM interactions and history navigation to (re)start root traces. */
export function installTraceTriggers(): () => void {
  if (typeof window === 'undefined') return () => {};

  const onInteract = () => startTrace('user-action');
  const onNav = () => startTrace('route-load');

  window.addEventListener('click', onInteract, { capture: true, passive: true });
  window.addEventListener('submit', onInteract, { capture: true, passive: true });
  window.addEventListener('popstate', onNav);

  // Patch pushState/replaceState to catch SPA navigation.
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (...args) {
    onNav();
    return origPush.apply(this, args as Parameters<typeof history.pushState>);
  };
  history.replaceState = function (...args) {
    onNav();
    return origReplace.apply(this, args as Parameters<typeof history.replaceState>);
  };

  return () => {
    window.removeEventListener('click', onInteract, { capture: true } as EventListenerOptions);
    window.removeEventListener('submit', onInteract, { capture: true } as EventListenerOptions);
    window.removeEventListener('popstate', onNav);
    history.pushState = origPush;
    history.replaceState = origReplace;
  };
}
